import { useEffect, useRef, useState } from "react";
import { listAllTasks } from "@/services/task.service";
import { listProjects, type Project } from "@/services/project.service";
import { listSprints, type Sprint } from "@/services/sprint.service";
import { listUsers, type UserProfile } from "@/services/user.service";
import { buildProdReport, type UserGroup } from "./groupProdTasks";

export interface ProdReportsState {
	// reference data
	projects: Project[];
	sprints: Sprint[];
	users: UserProfile[];
	refLoading: boolean;
	refError: string | null;

	// filters
	projectIds: string[];
	sprintId: string;
	userIds: string[];
	setProjectIds: (v: string[]) => void;
	setSprintId: (v: string) => void;
	setUserIds: (v: string[]) => void;

	// results
	groups: UserGroup[];
	sprint: Sprint | null;
	loading: boolean;
	error: string | null;
	applied: boolean;
	apply: () => Promise<void>;
}

export interface UseProdReportsOptions {
	initialProjectIds?: string[];
	initialSprintId?: string;
	initialUserIds?: string[];
	autoApply?: boolean;
}

export function useProdReports(
	opts: UseProdReportsOptions = {},
): ProdReportsState {
	const [projects, setProjects] = useState<Project[]>([]);
	const [sprints, setSprints] = useState<Sprint[]>([]);
	const [users, setUsers] = useState<UserProfile[]>([]);
	const [refLoading, setRefLoading] = useState(true);
	const [refError, setRefError] = useState<string | null>(null);

	const [projectIds, setProjectIds] = useState<string[]>(
		opts.initialProjectIds ?? [],
	);
	const [sprintId, setSprintId] = useState<string>(
		opts.initialSprintId ?? "",
	);
	const [userIds, setUserIds] = useState<string[]>(opts.initialUserIds ?? []);

	const [groups, setGroups] = useState<UserGroup[]>([]);
	const [sprint, setSprint] = useState<Sprint | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [applied, setApplied] = useState(false);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				setRefLoading(true);
				setRefError(null);
				const [proj, spr, usr] = await Promise.all([
					listProjects(),
					listSprints(),
					listUsers(),
				]);
				if (!mounted) return;
				setProjects(proj);
				setSprints(spr);
				setUsers(usr);
				if (!opts.initialSprintId) {
					const active = spr.find((s) => s.status === "active");
					if (active) setSprintId(active.id);
				}
			} catch (err) {
				if (mounted)
					setRefError(
						err instanceof Error
							? err.message
							: "Failed to load filters",
					);
			} finally {
				if (mounted) setRefLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	const apply = async () => {
		try {
			setLoading(true);
			setError(null);
			const tasks = await listAllTasks(
				sprintId ? { sprint_id: sprintId } : undefined,
			);
			const projectsById = new Map(projects.map((p) => [p.id, p.name]));
			setGroups(
				buildProdReport(tasks, projectsById, projectIds, userIds),
			);
			setSprint(sprints.find((s) => s.id === sprintId) ?? null);
			setApplied(true);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to load report",
			);
		} finally {
			setLoading(false);
		}
	};

	const autoApplied = useRef(false);
	useEffect(() => {
		if (!opts.autoApply || autoApplied.current) return;
		if (refLoading || refError) return;
		autoApplied.current = true;
		void apply();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [opts.autoApply, refLoading, refError]);

	return {
		projects,
		sprints,
		users,
		refLoading,
		refError,
		projectIds,
		sprintId,
		userIds,
		setProjectIds,
		setSprintId,
		setUserIds,
		groups,
		sprint,
		loading,
		error,
		applied,
		apply,
	};
}
